<template>
    <div>
        <span>
            <change-user-stage
                :userID="user.id"
                :data="user.stages"
                :currentUser="currentUser"
                @updated="$emit('updated')"
            />
            <div v-for="(stage, index) in stages" :key="index">
                <p class="mb-0" v-if="data[stage].id">
                    <small>
                        {{ $t(`stages.${stage}`) }}:
                        <strong>{{ data[stage].title }}</strong>
                        {{ $t('stages.from') }}
                        <strong>{{ formatDate(data[stage].from.date) }}</strong>
                        {{ $t('stages.to') }}
                        <strong>{{ formatDate(data[stage].to.date) }}</strong>
                    </small>
                </p>
                <p>
                    <b-link @click="toggleCollapse(stage)" v-if="data[stage].description">
                        <small v-if="!collapse[stage]">{{ $t('stages.showMore') }}</small>
                        <small v-else>{{ $t('stages.showLess') }}</small>
                    </b-link>
                    <b-collapse :id="stage" v-model="collapse[stage]">
                        <p class="mb-0">
                            <small class="text-muted">
                                {{ data[stage].description }}
                            </small>
                        </p>
                    </b-collapse>
                </p>
            </div>
            <div>
                <p
                    class="mb-0"
                    v-for="entryCriterion in data.entryCriteria"
                    :key="`entry-criteria-${entryCriterion.id}`"
                >
                    <i v-if="entryCriterion.status" class="far fa-check-circle align-middle text-success"></i>
                    <i v-else class="far fa-times-circle align-middle text-danger"></i>
                    <small class="text-muted">
                        {{ $t(`stages.eventHasCriteria.` + entryCriterion.title) }}
                    </small>
                </p>
            </div>
            <div class="d-flex justify-content-md-space-between">
                <div class="div-button">
                    <b-button
                        id="btn-target-return-stage"
                        v-if="isShowButtonStepReturn"
                        size="sm"
                        :disabled="!canReturnToPreviousStage"
                        variant="primary"
                        @click="requestStepReturnConfirm"
                        class="mt-4 mr-3"
                    >
                        {{ $t('stages.returnStep') }}
                        <i v-if="isSubmittingReturn" class="fa fa-spinner fa-spin px-2"></i>
                    </b-button>
                </div>
                <div class="div-button">
                    <b-button
                        id="btn-target-request-to-advance-stage"
                        v-if="isShowButtonRequestStepAdvance"
                        size="sm"
                        :disabled="isDisabledRequestStepAdvance"
                        variant="primary"
                        @click="requestStepAdvanceConfirm"
                        class="mt-4 ml-2 mr-2"
                    >
                        {{
                            isManager || isAdminOrLocalAdmin
                                ? $t('stages.managerConfirmsThatTheEmployeeIsAbleToEnterStep')
                                : $t('stages.requestStepAdvance')
                        }}
                        <i v-if="isSubmittingAdvance" class="fa fa-spinner fa-spin px-2"></i>
                    </b-button>
                </div>
            </div>
        </span>
    </div>
</template>

<script>
import api from '@/services/api'
import { getStringFromDateWithoutTime } from '@/lib/date'
import { find } from 'lodash'
import i18n from '@/i18n'
import notification, { success, danger } from '@/lib/notification'
import Swal from 'sweetalert2'
import { ChangeUserStage } from './components'

notification.init()

export default {
    name: 'Stages',
    components: {
        ChangeUserStage,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        currentUser: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            stages: ['previous', 'current', 'next'],
            collapse: {
                current: false,
                next: false,
            },
            isSubmittingReturn: false,
            isSubmittingAdvance: false,
            criteriaIgnore: [
                'automatically_advance_to_start_date',
                'employee_declare_able_to_enter_stage',
                'manager_declare_able_to_enter_stage',
            ],
        }
    },
    methods: {
        toggleCollapse(stage) {
            this.collapse[stage] = !this.collapse[stage]
        },
        formatDate(string) {
            return getStringFromDateWithoutTime(string, 'YYYY-MM-DD')
        },
        requestStepReturnConfirm() {
            this.swalUserMeetsTheRequirementsToReturnStep()
        },
        requestStepAdvanceConfirm() {
            const criterias = this.data.entryCriteria.filter((criteria) => {
                return !this.criteriaIgnore.includes(criteria.title) && !criteria.status
            })
            if (criterias) {
                const status = criterias.every((criteria) => {
                    return criteria.status
                })
                if (status) {
                    this.swalUserMeetsTheRequirementsToAdvanceStep()
                } else {
                    this.swalUserDoesNotMeetRequirementsToAdvanceStep(criterias)
                }
            } else {
                this.swalUserMeetsTheRequirementsToAdvanceStep()
            }
        },
        swalUserMeetsTheRequirementsToReturnStep() {
            const configurations = {
                type: 'warning',
                title: i18n.t('notification.areYouSure'),
                text: i18n.t(`stages.returnStage`, {
                    currentStage: this.data.current.title,
                    previousStage: this.data.previous.title,
                }),
                cancelButtonText: i18n.t(`notification.no`),
                confirmButtonText: i18n.t(`stages.returnStep`),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                showCancelButton: true,
            }
            Swal(configurations)
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.stepReturn()
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        swalUserMeetsTheRequirementsToAdvanceStep() {
            const configurations = {
                type: 'warning',
                title: i18n.t('notification.areYouSure'),
                text: i18n.t(`stages.requestChangeOfStage`, {
                    currentStage: this.data.current.title,
                    nextStage: this.data.next.title,
                }),
                cancelButtonText: i18n.t(`notification.no`),
                confirmButtonText: i18n.t(`stages.request`),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                showCancelButton: true,
            }
            Swal(configurations)
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.requestStepAdvance()
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        swalUserDoesNotMeetRequirementsToAdvanceStep(criterias) {
            const configurations = {
                type: 'warning',
                title: i18n.t(`stages.criteriasNotAchieved`),
                cancelButtonText: i18n.t(`modal.close`),
                showCancelButton: true,
                showConfirmButton: false,
            }
            configurations.html = criterias.reduce((html, criteria) => {
                return (
                    html +
                    `<p class="text-justify mb-0"> <i class="far fa-times-circle text-danger"></i>
                        ${i18n.t(`stages.eventHasCriteria.${criteria.title}`)}</p>`
                )
            }, `<p class="text-justify">${i18n.t(`stages.criteriasNotAchievedExplained`)}</p>`)
            Swal(configurations)
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.requestStepAdvance()
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        stepReturn() {
            this.isSubmittingReturn = true
            api.put(`/users/${this.user.id}/update-stage`, this.mapperUpdateReturnStageToAPI())
                .then(() => {
                    this.notificationEditedSuccessfully()
                    this.$emit('updated')
                })
                .catch((error) => {
                    this.notificationError()
                    console.log(error)
                })
                .finally(() => {
                    this.isSubmittingReturn = false
                })
        },
        requestStepAdvance() {
            this.isSubmittingAdvance = true
            api.put(`/users/${this.user.id}/update-stage`, this.mapperUpdateStageToAPI())
                .then(() => {
                    this.notificationEditedSuccessfully()
                    this.$emit('updated')
                })
                .catch((error) => {
                    this.notificationError()
                    console.log(error)
                })
                .finally(() => {
                    this.isSubmittingAdvance = false
                })
        },
        mapperUpdateReturnStageToAPI() {
            const eventId = this.data.previous.id
            return {
                event_id: eventId,
                type: 'return',
            }
        },
        mapperUpdateStageToAPI() {
            const eventId = this.data.next.id
            if (!this.isManager && !this.isAdminOrLocalAdmin) {
                return {
                    event_id: eventId,
                    ok_user: true,
                    type: 'request',
                }
            } else {
                return {
                    event_id: eventId,
                    ok_manager: true,
                    type: 'request',
                }
            }
        },
        notificationEditedSuccessfully() {
            success(
                i18n.t('notification.editedSuccessfully', {
                    item: i18n.t('stages.stage'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
    computed: {
        isManager() {
            return this.currentUser.id == this.user.manager.id
        },
        isAdminOrLocalAdmin() {
            return (
                (this.currentUser.role.name == 'administrator' ||
                    this.currentUser.role.name == 'local_administrator' ||
                    this.currentUser.role.name == 'god') &&
                !this.isManager &&
                this.currentUser.id != this.user.id
            )
        },
        isShowButtonRequestStepAdvance() {
            if (!this.data.entryCriteria.length) {
                return false
            }
            let criterion = null
            let entryCriteriaTitle = null
            entryCriteriaTitle =
                this.isManager || this.isAdminOrLocalAdmin
                    ? 'manager_declare_able_to_enter_stage'
                    : 'employee_declare_able_to_enter_stage'
            criterion = find(this.data.entryCriteria, ['title', entryCriteriaTitle])
            if (criterion == false) {
                criterion = find(this.data.entryCriteria, ['title', 'automatically_advance_to_start_date'])
                const dateNextStage = new Date(this.data.current.to.date).valueOf()
                const now = new Date().valueOf()
                return now >= dateNextStage ? true : false
            }
            return criterion ? !criterion.status : false
        },
        isDisabledRequestStepAdvance() {
            return this.isSubmittingAdvance
        },
        data() {
            return this.user.stages
        },
        canAlwaysReturnToPreviousStage() {
            return (
                this.currentUser.role.name === 'administrator' ||
                this.currentUser.role.name === 'local_administrator' ||
                this.currentUser.role.name === 'god' ||
                this.currentUser.is_target_master
            )
        },
        hasManagerPermission() {
            return this.isManager && this.currentUser.id != this.user.id
        },
        canReturnToPreviousStage() {
            if (this.canAlwaysReturnToPreviousStage) {
                return true
            }
            if (this.hasManagerPermission) {
                return this.user.stages.current.allowReturnToPreviousStage
            }
            return false
        },
        hasPreviousStage() {
            return this.user.stages.previous.id
        },
        isShowButtonStepReturn() {
            return this.hasPreviousStage ? true : false
        },
    },
}
</script>
<style lang="scss" scoped>
.div-button {
    width: 49%;
}
#btn-target-return-stage,
#btn-target-request-to-advance-stage {
    width: 99%;
}
#btn-target-return-stage:disabled {
    background-color: #6e6e6e;
    border-color: #6e6e6e;
    border-radius: 5px;
    color: #ffffff;
    opacity: 0.5;
}
</style>
